import { useEffect, useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { Button } from '@finn-auto/design-system/atoms/button';
import { fetchBryqOptions, type BryqKey, type BryqOptions } from '../../services/bryqApi';
import { fmtDate } from './format';

/**
 * Send one Bryq test to one candidate. Opened from the 🧠 chip.
 *
 * Reads, when it opens, which tests exist in Lever, the suggested one (Cognitive in the role's
 * language) and which tests this person already has — so a second invite for the same test is
 * visible before anyone clicks, and the server refuses it anyway.
 */
export function BryqModal({
  candidateName,
  opportunityId,
  onClose,
  onSend,
}: {
  candidateName: string;
  opportunityId: string;
  onClose: () => void;
  onSend: (test: BryqKey) => Promise<void>;
}) {
  const [options, setOptions] = useState<BryqOptions | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [picked, setPicked] = useState<BryqKey | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetchBryqOptions(opportunityId)
      .then((o) => live && setOptions(o))
      .catch((e) => live && setLoadError(e instanceof Error ? e.message : 'Could not read the Bryq tests from Lever.'));
    return () => {
      live = false;
    };
  }, [opportunityId]);

  const sentKeys = new Set((options?.sent ?? []).map((s) => s.key));
  const pickable = (key: BryqKey) =>
    Boolean(options?.tests.find((t) => t.key === key)?.available) && !sentKeys.has(key);
  const suggested = options && pickable(options.suggested) ? options.suggested : null;
  const choice = picked ?? suggested;

  const send = async () => {
    if (!choice) return;
    setSending(true);
    setError(null);
    try {
      await onSend(choice);
      setDone(options?.tests.find((t) => t.key === choice)?.label ?? 'Bryq');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send the Bryq test.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open onOpenChange={(open: boolean) => !open && onClose()}>
      <ModalContent className="!h-max !max-h-[calc(100dvh-2rem)] !w-[min(100vw-2rem,30rem)] gap-5 overflow-auto rounded p-6">
        <ModalHeader>
          <ModalTitle>Send Bryq to {candidateName} as a filter step</ModalTitle>
        </ModalHeader>
        {done ? (
          <div className="space-y-4 text-sm">
            <p>
              ✅ {candidateName} is in <strong>{done}</strong> in Lever. Bryq emails them the test link in the
              next few minutes. Their 🧠 chip turns green when the result is in.
            </p>
            <div className="flex justify-end">
              <Button onClick={onClose}>Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            <p className="text-gray-600">
              For when the result decides the next step: once {candidateName} finishes, they show up under
              Overdue → 🧠 Bryq assessments for you to move on or archive. To send Bryq together with an
              interview invite instead, tick “Send Bryq in parallel” in the invite dialog.
            </p>
            {!options && !loadError && <p className="text-gray-500">Reading Bryq tests from Lever…</p>}
            {loadError && <p className="text-red-600">{loadError}</p>}
            {options && options.sent.length > 0 && (
              <div className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
                <p className="mb-1 font-semibold">Already sent</p>
                {options.sent.map((s) => (
                  <p key={`${s.key}-${s.sentAt}`}>
                    {s.label} · {fmtDate(s.sentAt)}
                    {s.score !== null ? ` · score ${s.score}%` : ' · not finished yet'}
                  </p>
                ))}
              </div>
            )}
            {options && (
              <fieldset className="space-y-2">
                <legend className="mb-1 font-semibold">Which test?</legend>
                {options.tests.map((t) => {
                  const sent = sentKeys.has(t.key);
                  const off = !t.available || sent;
                  return (
                    <label key={t.key} className={`flex items-center gap-2 ${off ? 'text-gray-400' : ''}`}>
                      <input
                        type="radio"
                        name="bryq-test"
                        value={t.key}
                        checked={choice === t.key}
                        disabled={off}
                        onChange={() => setPicked(t.key)}
                      />
                      {t.label}
                      {!t.available && <span className="text-xs">· not set up in Lever yet</span>}
                      {sent && <span className="text-xs">· already sent</span>}
                      {t.key === options.suggested && !off && <span className="text-xs text-gray-500">· suggested</span>}
                    </label>
                  );
                })}
              </fieldset>
            )}
            {error && <p className="text-red-600">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} disabled={sending}>
                Cancel
              </Button>
              <Button onClick={send} disabled={!choice || sending}>
                {sending ? 'Sending…' : 'Send Bryq'}
              </Button>
            </div>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
}
